import React, { useState, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  FormLabel,
  Chip,
  CircularProgress,
  Snackbar,
  Alert,
  alpha,
  Popover,
  InputAdornment,
  Divider,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import SearchIcon from "@mui/icons-material/Search";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import CloudIcon from "@mui/icons-material/Cloud";
import PublicIcon from "@mui/icons-material/Public";
import {
  getCustomerMetadata,
  updateRule,
  clearBillingManagementMessages,
} from "../../store/billingManagement/billingManagementSlice";
import { getCustomerMetadata as getCustomerMetadataApi } from "../../helpers/auth_aws_helper";
import { getApiErrorMessage } from "../../helpers/aws_api_helper";

const EditBillingRuleModal = ({ open, onClose, rule, customerOrgCode }) => {
  const dispatch = useDispatch();

  // Redux state
  const {
    metadata,
    isFetchingMetadata,
    isUpdatingRule,
    updateRuleSuccess,
    updateRuleError,
  } = useSelector((state) => state.billingManagement);

  // Local state
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    status: "ACTIVE",
    frequency: "RECURRING",
    start_date: "",
    end_date: "",
    allAccounts: true,
    selectedAccounts: [],
    selectedServices: [],
    selectedRegions: [],
    scopeOperatorAccounts: "INCLUDE",
    scopeOperatorServices: "INCLUDE",
    scopeOperatorRegions: "INCLUDE",
    parameters: {},
  });

  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const initialFormDataRef = useRef(null);
  const prevOpenRef = useRef(false);
  const [accountsPopover, setAccountsPopover] = useState({ open: false, anchorEl: null });
  const [servicesPopover, setServicesPopover] = useState({ open: false, anchorEl: null });
  const [regionsPopover, setRegionsPopover] = useState({ open: false, anchorEl: null });

  // Service and Region metadata
  const [serviceCodes, setServiceCodes] = useState([]);
  const [regions, setRegions] = useState([]);
  const [isFetchingServiceCodes, setIsFetchingServiceCodes] = useState(false);
  const [isFetchingRegions, setIsFetchingRegions] = useState(false);
  const serviceCodesFetchStartedRef = useRef(false);
  const regionsFetchStartedRef = useRef(false);

  // Reset service/region fetch refs when modal closes so next open can fetch again
  useEffect(() => {
    if (!open) {
      serviceCodesFetchStartedRef.current = false;
      regionsFetchStartedRef.current = false;
    }
  }, [open]);

  // Fetch metadata only on transition from closed → open (once per open; avoids infinite calls e.g. Adjustment)
  useEffect(() => {
    const justOpened = open && !prevOpenRef.current;
    if (open) prevOpenRef.current = true;
    if (!open) prevOpenRef.current = false;
    if (justOpened && customerOrgCode) {
      dispatch(getCustomerMetadata({ customerOrgCode, params: { kind: "rule_definitions" } }));
    }
  }, [open, customerOrgCode, dispatch]);

  // Fetch service codes and regions at most once per open (refs prevent re-runs from metadata/state churn)
  useEffect(() => {
    if (!open || !customerOrgCode || !formData.selectedCategory || !metadata) return;

    const categoryData = metadata.categories?.find((cat) => cat.category === formData.selectedCategory);
    const allowedScopes = categoryData?.allowed_scopes || [];
    const allowsService = allowedScopes.some((scope) => scope.scope_type === "SERVICE");
    const allowsRegion = allowedScopes.some((scope) => scope.scope_type === "REGION");

    if (allowsService && serviceCodes.length === 0 && !serviceCodesFetchStartedRef.current) {
      serviceCodesFetchStartedRef.current = true;
      setIsFetchingServiceCodes(true);
      getCustomerMetadataApi(customerOrgCode, { kind: "service_codes" })
        .then((response) => {
          if (response.status === 200) {
            const { status, ...responseData } = response;
            setServiceCodes(responseData.service_codes || []);
          }
        })
        .catch((error) => {
          console.error("Error fetching service codes:", error);
        })
        .finally(() => {
          setIsFetchingServiceCodes(false);
        });
    }

    if (allowsRegion && regions.length === 0 && !regionsFetchStartedRef.current) {
      regionsFetchStartedRef.current = true;
      setIsFetchingRegions(true);
      getCustomerMetadataApi(customerOrgCode, { kind: "regions" })
        .then((response) => {
          if (response.status === 200) {
            const { status, ...responseData } = response;
            setRegions(responseData.regions || []);
          }
        })
        .catch((error) => {
          console.error("Error fetching regions:", error);
        })
        .finally(() => {
          setIsFetchingRegions(false);
        });
    }
  }, [open, customerOrgCode, formData.selectedCategory, metadata, serviceCodes.length, regions.length]);

  // Parse rule_config to extract parameter values
  const parseRuleConfig = (ruleConfig, ruleCategory, ruleType) => {
    if (!ruleConfig) return {};

    const parameters = {};

    // Handle percentage values (e.g., "9%", "2.5%")
    if (ruleConfig.includes("%")) {
      const percentValue = parseFloat(ruleConfig.replace("%", "").trim());
      if (!isNaN(percentValue)) {
        parameters.percent = percentValue;
      }
    }
    // Handle currency values (e.g., "$1000", "$200")
    else if (ruleConfig.includes("$")) {
      const amountValue = parseFloat(ruleConfig.replace("$", "").replace(/,/g, "").trim());
      if (!isNaN(amountValue)) {
        parameters.amount = amountValue;
      }
    }
    // Handle boolean values (e.g., "Yes", "No", "true", "false")
    else if (ruleConfig.toLowerCase() === "yes" || ruleConfig.toLowerCase() === "true") {
      parameters.enabled = true;
    } else if (ruleConfig.toLowerCase() === "no" || ruleConfig.toLowerCase() === "false") {
      parameters.enabled = false;
    }
    // Try to parse as number
    else {
      const numValue = parseFloat(ruleConfig);
      if (!isNaN(numValue)) {
        // Determine if it's percent or amount based on rule type
        if (ruleType === "FIXED_COST") {
          parameters.amount = numValue;
        } else {
          parameters.percent = numValue;
        }
      }
    }

    return parameters;
  };

  // Pre-fill form data when rule is provided (use rule_id so we don't re-run when parent passes new rule object ref)
  const ruleId = rule?.rule_id;
  useEffect(() => {
    if (!open || !rule || !metadata) {
      if (!open) initialFormDataRef.current = null;
      return;
    }
    // Extract scopes
    const scopes = rule.scopes || [];
    const linkedAccountScopes = scopes.filter((s) => s.scope_type === "LINKED_ACCOUNT");
    const serviceScopes = scopes.filter((s) => s.scope_type === "SERVICE");
    const regionScopes = scopes.filter((s) => s.scope_type === "REGION");

    // Linked accounts: prefer scopes, then fallback to linked_account_ids
    let allAccounts = true;
    let selectedAccounts = [];
    if (linkedAccountScopes.length > 0) {
      const hasAll = linkedAccountScopes.some((s) => s.scope_value === "ALL");
      if (hasAll) {
        allAccounts = true;
        selectedAccounts = [];
      } else {
        allAccounts = false;
        selectedAccounts = linkedAccountScopes.map((s) => s.scope_value).filter(Boolean);
      }
    } else if (rule.linked_account_ids && Array.isArray(rule.linked_account_ids)) {
      if (rule.linked_account_ids.includes("ALL") || rule.linked_account_ids.length === 0) {
        allAccounts = true;
        selectedAccounts = [];
      } else {
        allAccounts = false;
        selectedAccounts = rule.linked_account_ids;
      }
    }

    // Extract dates
      const startDate = rule.start_date || rule.start_month || "";
      const endDate = rule.end_date || rule.end_month || "";
      const noEndDate = !endDate;

      // Extract parameters - prefer configuration.parameters, fallback to parsing rule_config
      let parameters = rule.configuration?.parameters;

      if (!parameters || Object.keys(parameters).length === 0) {
        // If configuration parameters are missing, derive from rule_config as a best-effort fallback
        parameters = rule.rule_config
          ? parseRuleConfig(rule.rule_config, rule.rule_category, rule.rule_type)
          : {};
      }

      const scopeOperatorAccounts =
        linkedAccountScopes.length > 0 && linkedAccountScopes[0].operator
          ? linkedAccountScopes[0].operator
          : rule.scope_operator_accounts || rule.scopeOperatorAccounts || "INCLUDE";
      const scopeOperatorServices =
        serviceScopes.length > 0 && serviceScopes[0].operator
          ? serviceScopes[0].operator
          : rule.scope_operator_services || rule.scopeOperatorServices || "INCLUDE";
      const scopeOperatorRegions =
        regionScopes.length > 0 && regionScopes[0].operator
          ? regionScopes[0].operator
          : rule.scope_operator_regions || rule.scopeOperatorRegions || "INCLUDE";

      const initialData = {
        name: rule.rule_name || rule.name || "",
        description: rule.description || "",
        status: rule.status || "ACTIVE",
        frequency: rule.frequency || "RECURRING",
        start_date: startDate ? startDate.substring(0, 7) : "",
        end_date: endDate ? endDate.substring(0, 7) : "",
        allAccounts,
        selectedAccounts,
        selectedServices: serviceScopes.map((s) => s.scope_value),
        selectedRegions: regionScopes.map((s) => s.scope_value),
        scopeOperatorAccounts,
        scopeOperatorServices,
        scopeOperatorRegions,
        parameters,
        selectedCategory: rule.rule_category,
        selectedType: rule.rule_type,
        cost_measurement: rule.cost_measurement || "UNBLENDED",
      };
    setFormData(initialData);
    initialFormDataRef.current = JSON.parse(JSON.stringify(initialData));
  }, [open, ruleId, metadata]);

  // Handle success/error
  useEffect(() => {
    if (updateRuleSuccess) {
      setSnackbar({
        open: true,
        message: "Billing rule updated successfully!",
        severity: "success",
      });
      setTimeout(() => {
        onClose();
        dispatch(clearBillingManagementMessages());
      }, 1500);
    }
  }, [updateRuleSuccess, onClose, dispatch]);

  useEffect(() => {
    if (updateRuleError) {
      const message = typeof updateRuleError === "string" ? updateRuleError : (getApiErrorMessage(updateRuleError) || "Failed to update billing rule.");
      setSnackbar({
        open: true,
        message,
        severity: "error",
      });
      dispatch(clearBillingManagementMessages());
    }
  }, [updateRuleError, dispatch]);

  // Get available types for a category
  const getAvailableTypes = (category) => {
    if (!category) return [];
    return metadata?.categories?.find((cat) => cat.category === category)?.types || [];
  };

  // Get allowed scopes for a category
  const getAllowedScopes = (category) => {
    if (!category) return [];
    const categoryData = metadata?.categories?.find((cat) => cat.category === category);
    return categoryData?.allowed_scopes || [];
  };

  // Get required parameters for selected type
  const getRequiredParameters = () => {
    if (!formData.selectedCategory || !formData.selectedType) return [];
    const types = getAvailableTypes(formData.selectedCategory);
    return types.find((type) => type.type === formData.selectedType)?.required_parameters || [];
  };

  // Check if description is required
  const isDescriptionRequired = () => {
    const requiredParams = getRequiredParameters();
    return requiredParams.some((p) => p.key === "description" && p.required);
  };

  // Update form field
  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Update parameter
  const updateParameter = (key, value) => {
    setFormData((prev) => ({
      ...prev,
      parameters: { ...prev.parameters, [key]: value },
    }));
  };

  // Build scopes array (operator: INCLUDE or EXCLUDE per scope type)
  const buildScopes = () => {
    const scopes = [];
    const accountOp = formData.scopeOperatorAccounts === "EXCLUDE" ? "EXCLUDE" : "INCLUDE";
    const serviceOp = formData.scopeOperatorServices === "EXCLUDE" ? "EXCLUDE" : "INCLUDE";
    const regionOp = formData.scopeOperatorRegions === "EXCLUDE" ? "EXCLUDE" : "INCLUDE";

    // Linked accounts (do not send scope when "All" is selected — omit LINKED_ACCOUNT scope for ALL)
    if (!formData.allAccounts && formData.selectedAccounts.length > 0) {
      formData.selectedAccounts.forEach((account) => {
        if (String(account).toUpperCase() === "ALL") return;
        scopes.push({
          scope_type: "LINKED_ACCOUNT",
          scope_value: account,
          operator: accountOp,
        });
      });
    }

    // Services
    formData.selectedServices.forEach((service) => {
      scopes.push({
        scope_type: "SERVICE",
        scope_value: service,
        operator: serviceOp,
      });
    });

    // Regions
    formData.selectedRegions.forEach((region) => {
      scopes.push({
        scope_type: "REGION",
        scope_value: region,
        operator: regionOp,
      });
    });

    return scopes;
  };

  // Format date for API: must be YYYY-MM-DD (same format as end_date)
  const formatDateForAPI = (dateString) => {
    if (!dateString) return null;
    const s = String(dateString).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    if (/^\d{4}-\d{2}$/.test(s)) return `${s}-01`;
    return s.length >= 10 ? s.substring(0, 10) : `${s}-01`;
  };

  // Validate form
  const isFormValid = useMemo(() => {
    if (!formData.name) return false;
    if (isDescriptionRequired() && !formData.description) return false;

    // For RECURRING, start_date is required
    if (formData.frequency === "RECURRING" && !formData.start_date) return false;

    // Check required parameters (for EXCLUSION, do not require boolean params as they are hidden)
    const requiredParams = getRequiredParameters();
    const paramsToValidate =
      formData.selectedCategory === "EXCLUSION"
        ? requiredParams.filter((p) => p.type !== "boolean")
        : requiredParams;
    for (const param of paramsToValidate) {
      if (param.required) {
        const value = formData.parameters[param.key];
        if (value === undefined || value === null || value === "") {
          return false;
        }
      }
    }

    return true;
  }, [formData]);

  // True if user has changed any editable field from the initial (loaded) values
  const hasFormChanges = useMemo(() => {
    const initial = initialFormDataRef.current;
    if (!initial) return false;
    const editable = {
      name: formData.name,
      description: formData.description,
      status: formData.status,
      frequency: formData.frequency,
      start_date: formData.start_date,
      end_date: formData.end_date,
      allAccounts: formData.allAccounts,
      selectedAccounts: [...(formData.selectedAccounts || [])].sort(),
      selectedServices: [...(formData.selectedServices || [])].sort(),
      selectedRegions: [...(formData.selectedRegions || [])].sort(),
      scopeOperatorAccounts: formData.scopeOperatorAccounts,
      scopeOperatorServices: formData.scopeOperatorServices,
      scopeOperatorRegions: formData.scopeOperatorRegions,
      parameters: formData.parameters || {},
    };
    const initialEditable = {
      name: initial.name,
      description: initial.description,
      status: initial.status,
      frequency: initial.frequency,
      start_date: initial.start_date,
      end_date: initial.end_date,
      allAccounts: initial.allAccounts,
      selectedAccounts: [...(initial.selectedAccounts || [])].sort(),
      selectedServices: [...(initial.selectedServices || [])].sort(),
      selectedRegions: [...(initial.selectedRegions || [])].sort(),
      scopeOperatorAccounts: initial.scopeOperatorAccounts,
      scopeOperatorServices: initial.scopeOperatorServices,
      scopeOperatorRegions: initial.scopeOperatorRegions,
      parameters: initial.parameters || {},
    };
    return JSON.stringify(editable) !== JSON.stringify(initialEditable);
  }, [formData]);

  // Handle submit
  const handleSubmit = () => {
    if (!isFormValid) {
      setSnackbar({
        open: true,
        message: "Please fill in all required fields.",
        severity: "error",
      });
      return;
    }

    const payload = {
      name: formData.name,
      status: formData.status, // keep status in payload even though it's not editable in UI
      scopes: buildScopes(),
      configuration: {
        parameters: formData.parameters,
      },
    };

    // Add description if provided
    if (formData.description) {
      payload.description = formData.description;
    }

    // For RECURRING rules, add dates (omit for ONE_TIME)
    if (formData.frequency === "RECURRING") {
      payload.start_date = formatDateForAPI(formData.start_date);
      payload.end_date = formatDateForAPI(formData.end_date);
    }

    dispatch(updateRule({ customerOrgCode, ruleId: rule.rule_id, ruleData: payload }));
  };

  // Get category and type info
  const categoryData = metadata?.categories?.find((cat) => cat.category === formData.selectedCategory);
  const typeData = categoryData?.types?.find((type) => type.type === formData.selectedType);
  const allowedScopes = getAllowedScopes(formData.selectedCategory);
  const showServiceField = allowedScopes.some((scope) => scope.scope_type === "SERVICE");
  const showRegionField = allowedScopes.some((scope) => scope.scope_type === "REGION");
  const requiredParameters = getRequiredParameters();
  const isRecurring = formData.frequency === "RECURRING";
  const isOneTime = formData.frequency === "ONE_TIME";

  // Get available accounts from metadata
  const availableAccounts = metadata?.accounts || [];

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontSize: "1.25rem",
            fontWeight: 600,
            color: "#1e293b",
            pb: 1.5,
            borderBottom: "1px solid #e2e8f0",
          }}
        >
          Edit Billing Rule
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {isFetchingMetadata ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 4 }}>
              <CircularProgress size={40} sx={{ color: "#3b82f6" }} />
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {/* Immutable Fields (Display Only) */}
              <Box>
                <Typography sx={{ fontSize: "0.875rem", color: "#64748b", mb: 1 }}>
                  Rule Category
                </Typography>
                <TextField
                  value={categoryData?.label || formData.selectedCategory || "--"}
                  disabled
                  fullWidth
                  size="small"
                  sx={{
                    "& .MuiInputBase-input.Mui-disabled": {
                      WebkitTextFillColor: "#64748b",
                      backgroundColor: "#f8fafc",
                    },
                  }}
                />
              </Box>

              <Box>
                <Typography sx={{ fontSize: "0.875rem", color: "#64748b", mb: 1 }}>
                  Rule Type
                </Typography>
                <TextField
                  value={typeData?.label || formData.selectedType || "--"}
                  disabled
                  fullWidth
                  size="small"
                  sx={{
                    "& .MuiInputBase-input.Mui-disabled": {
                      WebkitTextFillColor: "#64748b",
                      backgroundColor: "#f8fafc",
                    },
                  }}
                />
              </Box>

              {isOneTime && (
                <Box>
                  <Typography sx={{ fontSize: "0.875rem", color: "#64748b", mb: 1 }}>
                    Frequency
                  </Typography>
                  <TextField
                    value="One-time"
                    disabled
                    fullWidth
                    size="small"
                    sx={{
                      "& .MuiInputBase-input.Mui-disabled": {
                        WebkitTextFillColor: "#64748b",
                        backgroundColor: "#f8fafc",
                      },
                    }}
                  />
                </Box>
              )}

              {/* Configuration Parameters - boolean as checkbox, others as text/number; hide boolean fields for exclusion */}
              {(() => {
                const paramsToShow =
                  formData.selectedCategory === "EXCLUSION"
                    ? requiredParameters.filter((p) => p.type !== "boolean")
                    : requiredParameters;
                return paramsToShow.length > 0 ? (
                <Box>
                  <Typography sx={{ fontSize: "0.875rem", color: "#374151", mb: 1.5, fontWeight: 500 }}>
                    Configuration Parameters
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    {paramsToShow.map((param) => (
                      <Box key={param.key}>
                        {param.type === "boolean" ? (
                          <Box
                            sx={{
                              p: 2,
                              border: "1px solid #e2e8f0",
                              borderRadius: "8px",
                              backgroundColor: "#ffffff",
                            }}
                          >
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={Boolean(formData.parameters[param.key])}
                                  onChange={(e) => updateParameter(param.key, e.target.checked)}
                                  size="small"
                                />
                              }
                              label={
                                <Box>
                                  <Typography sx={{ fontSize: "0.875rem", fontWeight: 500 }}>
                                    {param.label}
                                    {param.required && (
                                      <Typography component="span" sx={{ color: "#ef4444", ml: 0.5 }}>
                                        *
                                      </Typography>
                                    )}
                                  </Typography>
                                  {param.description && (
                                    <Typography sx={{ fontSize: "0.8125rem", color: "#64748b", mt: 0.5 }}>
                                      {param.description}
                                    </Typography>
                                  )}
                                </Box>
                              }
                            />
                          </Box>
                        ) : (
                          <TextField
                            label={param.label}
                            value={formData.parameters[param.key] ?? ""}
                            onChange={(e) => {
                              let value = e.target.value;
                              if (param.type === "number") {
                                value = value === "" ? "" : parseFloat(value) || 0;
                              }
                              updateParameter(param.key, value);
                            }}
                            type={param.type === "number" ? "number" : "text"}
                            required={param.required}
                            fullWidth
                            size="small"
                            multiline={param.type === "string" && param.label?.toLowerCase().includes("description")}
                            rows={param.type === "string" && param.label?.toLowerCase().includes("description") ? 3 : 1}
                            helperText={param.description}
                          />
                        )}
                      </Box>
                    ))}
                  </Box>
                </Box>
              ) : null;
              })()}

              {/* Rule Name */}
              <TextField
                label="Billing Rule Name"
                value={formData.name}
                onChange={(e) => updateField("name", e.target.value)}
                required
                fullWidth
                size="small"
              />

              {/* Description */}
              <TextField
                label="Description"
                value={formData.description}
                onChange={(e) => updateField("description", e.target.value)}
                required={isDescriptionRequired()}
                fullWidth
                size="small"
                multiline
                rows={3}
                helperText={isDescriptionRequired() ? "Description is required for this rule type." : ""}
              />

              {/* Linked Accounts */}
              <Box>
                <Typography sx={{ fontSize: "0.875rem", color: "#374151", mb: 1, fontWeight: 500 }}>
                  Linked Accounts
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.allAccounts}
                      onChange={(e) => {
                        updateField("allAccounts", e.target.checked);
                        if (e.target.checked) {
                          updateField("selectedAccounts", []);
                        }
                      }}
                      size="small"
                    />
                  }
                  label="All Accounts"
                />
                {!formData.allAccounts && (
                  <Box sx={{ mt: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                      <Button
                        variant="outlined"
                        onClick={(e) => setAccountsPopover({ open: true, anchorEl: e.currentTarget })}
                        endIcon={<ArrowDropDownIcon />}
                        sx={{
                          textTransform: "none",
                          borderColor: "#e2e8f0",
                          color: "#475569",
                          "&:hover": {
                            borderColor: "#cbd5e1",
                            backgroundColor: "#f8fafc",
                          },
                        }}
                      >
                        {formData.selectedAccounts.length > 0
                          ? `${formData.selectedAccounts.length} Account${formData.selectedAccounts.length !== 1 ? "s" : ""} Selected`
                          : "Select Accounts"}
                      </Button>
                      <FormControl size="small" sx={{ minWidth: 100 }}>
                        <Select
                          value={formData.scopeOperatorAccounts || "INCLUDE"}
                          onChange={(e) => updateField("scopeOperatorAccounts", e.target.value)}
                          displayEmpty
                          sx={{ fontSize: "0.8125rem", height: 36 }}
                        >
                          <MenuItem value="INCLUDE">Include</MenuItem>
                          <MenuItem value="EXCLUDE">Exclude</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                    {formData.selectedAccounts.length > 0 && (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }}>
                        {formData.selectedAccounts.map((account) => (
                          <Chip
                            key={account}
                            label={account}
                            onDelete={() => {
                              const newAccounts = formData.selectedAccounts.filter((a) => a !== account);
                              updateField("selectedAccounts", newAccounts);
                              if (newAccounts.length === 0) {
                                updateField("allAccounts", true);
                              }
                            }}
                            size="small"
                            sx={{
                              backgroundColor: "#eff6ff",
                              color: "#3b82f6",
                              "& .MuiChip-deleteIcon": {
                                color: "#3b82f6",
                              },
                            }}
                          />
                        ))}
                      </Box>
                    )}
                  </Box>
                )}
              </Box>

              {/* Service and Region Filters */}
              {(showServiceField || showRegionField) && (
                <Box>
                  <Typography sx={{ fontSize: "0.875rem", color: "#374151", mb: 1.5, fontWeight: 500 }}>
                    Scope Filters
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    {showServiceField && (
                      <Box>
                        <Button
                          variant="outlined"
                          startIcon={<CloudIcon />}
                          onClick={(e) => setServicesPopover({ open: true, anchorEl: e.currentTarget })}
                          endIcon={<ArrowDropDownIcon />}
                          sx={{
                            textTransform: "none",
                            borderColor: "#e2e8f0",
                            color: "#475569",
                            "&:hover": {
                              borderColor: "#cbd5e1",
                              backgroundColor: "#f8fafc",
                            },
                          }}
                        >
                          {formData.selectedServices.length > 0
                            ? `${formData.selectedServices.length} Service${formData.selectedServices.length !== 1 ? "s" : ""} Selected`
                            : "Select AWS Services"}
                        </Button>
                        {formData.selectedServices.length > 0 && (
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }}>
                            {formData.selectedServices.map((service) => (
                              <Chip
                                key={service}
                                label={service}
                                onDelete={() => {
                                  updateField(
                                    "selectedServices",
                                    formData.selectedServices.filter((s) => s !== service)
                                  );
                                }}
                                size="small"
                                sx={{
                                  backgroundColor: "#eff6ff",
                                  color: "#3b82f6",
                                  "& .MuiChip-deleteIcon": {
                                    color: "#3b82f6",
                                  },
                                }}
                              />
                            ))}
                          </Box>
                        )}
                      </Box>
                    )}

                    {showRegionField && (
                      <Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                          <Button
                            variant="outlined"
                            startIcon={<PublicIcon />}
                            onClick={(e) => setRegionsPopover({ open: true, anchorEl: e.currentTarget })}
                            endIcon={<ArrowDropDownIcon />}
                            sx={{
                              textTransform: "none",
                              borderColor: "#e2e8f0",
                              color: "#475569",
                              "&:hover": {
                                borderColor: "#cbd5e1",
                                backgroundColor: "#f8fafc",
                              },
                            }}
                          >
                            {formData.selectedRegions.length > 0
                              ? `${formData.selectedRegions.length} Region${formData.selectedRegions.length !== 1 ? "s" : ""} Selected`
                              : "Select AWS Regions"}
                          </Button>
                          <FormControl size="small" sx={{ minWidth: 100 }}>
                            <Select
                              value={formData.scopeOperatorRegions || "INCLUDE"}
                              onChange={(e) => updateField("scopeOperatorRegions", e.target.value)}
                              displayEmpty
                              sx={{ fontSize: "0.8125rem", height: 36 }}
                            >
                              <MenuItem value="INCLUDE">Include</MenuItem>
                              <MenuItem value="EXCLUDE">Exclude</MenuItem>
                            </Select>
                          </FormControl>
                        </Box>
                        {formData.selectedRegions.length > 0 && (
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }}>
                            {formData.selectedRegions.map((region) => (
                              <Chip
                                key={region}
                                label={region}
                                onDelete={() => {
                                  updateField(
                                    "selectedRegions",
                                    formData.selectedRegions.filter((r) => r !== region)
                                  );
                                }}
                                size="small"
                                sx={{
                                  backgroundColor: "#eff6ff",
                                  color: "#3b82f6",
                                  "& .MuiChip-deleteIcon": {
                                    color: "#3b82f6",
                                  },
                                }}
                              />
                            ))}
                          </Box>
                        )}
                      </Box>
                    )}
                  </Box>
                </Box>
              )}

              {/* Frequency - Immutable (Display Only) */}
              <Box>
                <Typography sx={{ fontSize: "0.875rem", color: "#64748b", mb: 1 }}>
                  Frequency
                </Typography>
                <TextField
                  value={formData.frequency === "RECURRING" ? "Recurring" : formData.frequency === "ONE_TIME" ? "One-time" : formData.frequency || "--"}
                  disabled
                  fullWidth
                  size="small"
                  sx={{
                    "& .MuiInputBase-input.Mui-disabled": {
                      WebkitTextFillColor: "#64748b",
                      backgroundColor: "#f8fafc",
                    },
                  }}
                />
              </Box>

              {/* Date Range - Only for RECURRING */}
              {isRecurring && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <Box>
                    <Typography sx={{ fontSize: "0.875rem", color: "#374151", mb: 1, fontWeight: 500 }}>
                      Start Month <span style={{ color: "#ef4444" }}>*</span>
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <FormControl size="small" sx={{ flex: 1 }} required>
                        <InputLabel>Month</InputLabel>
                        <Select
                          value={formData.start_date ? (parseInt(formData.start_date.split("-")[1]) - 1) : ""}
                          label="Month"
                          onChange={(e) => {
                            const month = e.target.value;
                            const year = formData.start_date ? parseInt(formData.start_date.split("-")[0]) : new Date().getFullYear();
                            const monthStr = String(month + 1).padStart(2, "0");
                            updateField("start_date", `${year}-${monthStr}`);
                          }}
                        >
                          {[
                            "January", "February", "March", "April", "May", "June",
                            "July", "August", "September", "October", "November", "December"
                          ].map((month, index) => (
                            <MenuItem key={index} value={index}>
                              {month}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ flex: 1 }} required>
                        <InputLabel>Year</InputLabel>
                        <Select
                          value={formData.start_date ? parseInt(formData.start_date.split("-")[0]) : new Date().getFullYear()}
                          label="Year"
                          onChange={(e) => {
                            const year = e.target.value;
                            const month = formData.start_date ? (parseInt(formData.start_date.split("-")[1]) - 1) : new Date().getMonth();
                            const monthStr = String(month + 1).padStart(2, "0");
                            updateField("start_date", `${year}-${monthStr}`);
                          }}
                        >
                          {Array.from({ length: new Date().getFullYear() + 11 - 2000 }, (_, i) => {
                            const year = 2000 + i;
                            return (
                              <MenuItem key={year} value={year}>
                                {year}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </FormControl>
                    </Box>
                  </Box>

                  <Box>
                    <Typography sx={{ fontSize: "0.875rem", color: "#374151", mb: 1, fontWeight: 500 }}>
                      End Month <span style={{ color: "#ef4444" }}>*</span>
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <FormControl size="small" sx={{ flex: 1 }} required>
                        <InputLabel>Month</InputLabel>
                        <Select
                          value={formData.end_date ? (parseInt(formData.end_date.split("-")[1], 10) - 1) : ""}
                          label="Month"
                          onChange={(e) => {
                            const month = e.target.value;
                            const year = formData.end_date ? parseInt(formData.end_date.split("-")[0], 10) : new Date().getFullYear();
                            const monthStr = String(month + 1).padStart(2, "0");
                            updateField("end_date", `${year}-${monthStr}`);
                          }}
                        >
                          {[
                            "January", "February", "March", "April", "May", "June",
                            "July", "August", "September", "October", "November", "December"
                          ].map((month, index) => (
                            <MenuItem key={index} value={index}>
                              {month}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ flex: 1 }} required>
                        <InputLabel>Year</InputLabel>
                        <Select
                          value={formData.end_date ? parseInt(formData.end_date.split("-")[0], 10) : new Date().getFullYear()}
                          label="Year"
                          onChange={(e) => {
                            const year = e.target.value;
                            const month = formData.end_date ? (parseInt(formData.end_date.split("-")[1], 10) - 1) : new Date().getMonth();
                            const monthStr = String(month + 1).padStart(2, "0");
                            updateField("end_date", `${year}-${monthStr}`);
                          }}
                        >
                          {Array.from({ length: new Date().getFullYear() + 11 - 2000 }, (_, i) => {
                            const year = 2000 + i;
                            return (
                              <MenuItem key={year} value={year}>
                                {year}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </FormControl>
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1.5, borderTop: "1px solid #e2e8f0" }}>
          <Button
            onClick={onClose}
            disabled={isUpdatingRule}
            sx={{
              textTransform: "none",
              color: "#64748b",
              fontSize: "0.875rem",
              fontWeight: 500,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isUpdatingRule || !isFormValid || !hasFormChanges}
            variant="contained"
            sx={{
              textTransform: "none",
              backgroundColor: "#3b82f6",
              color: "#ffffff",
              fontSize: "0.875rem",
              fontWeight: 500,
              "&:hover": {
                backgroundColor: "#2563eb",
              },
              "&.Mui-disabled": {
                backgroundColor: "#cbd5e1",
                color: "#ffffff",
              },
            }}
          >
            {isUpdatingRule ? "Updating..." : "Update Rule"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Accounts Popover */}
      <Popover
        open={accountsPopover.open}
        anchorEl={accountsPopover.anchorEl}
        onClose={() => setAccountsPopover({ open: false, anchorEl: null })}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{
          sx: {
            width: 300,
            maxHeight: 400,
            borderRadius: "8px",
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
          },
        }}
      >
        <Box sx={{ p: 1.5 }}>
          <TextField
            size="small"
            placeholder="Search accounts..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: "#9ca3af" }} />
                </InputAdornment>
              ),
            }}
            fullWidth
            sx={{ mb: 1 }}
          />
          <Box sx={{ maxHeight: 300, overflowY: "auto" }}>
            {availableAccounts.map((account) => (
              <FormControlLabel
                key={account}
                control={
                  <Checkbox
                    checked={formData.selectedAccounts.includes(account)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        updateField("selectedAccounts", [...formData.selectedAccounts, account]);
                        updateField("allAccounts", false);
                      } else {
                        const newAccounts = formData.selectedAccounts.filter((a) => a !== account);
                        updateField("selectedAccounts", newAccounts);
                        if (newAccounts.length === 0) {
                          updateField("allAccounts", true);
                        }
                      }
                    }}
                    size="small"
                  />
                }
                label={account}
                sx={{
                  display: "flex",
                  width: "100%",
                  m: 0,
                  py: 0.5,
                }}
              />
            ))}
          </Box>
        </Box>
      </Popover>

      {/* Services Popover */}
      <Popover
        open={servicesPopover.open}
        anchorEl={servicesPopover.anchorEl}
        onClose={() => setServicesPopover({ open: false, anchorEl: null })}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{
          sx: {
            width: 300,
            maxHeight: 400,
            borderRadius: "8px",
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
          },
        }}
      >
        <Box sx={{ p: 1.5 }}>
          <TextField
            size="small"
            placeholder="Search services..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: "#9ca3af" }} />
                </InputAdornment>
              ),
            }}
            fullWidth
            sx={{ mb: 1 }}
          />
          <Box sx={{ maxHeight: 300, overflowY: "auto" }}>
            {serviceCodes.map((service) => (
              <FormControlLabel
                key={service}
                control={
                  <Checkbox
                    checked={formData.selectedServices.includes(service)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        updateField("selectedServices", [...formData.selectedServices, service]);
                      } else {
                        updateField("selectedServices", formData.selectedServices.filter((s) => s !== service));
                      }
                    }}
                    size="small"
                  />
                }
                label={service}
                sx={{
                  display: "flex",
                  width: "100%",
                  m: 0,
                  py: 0.5,
                }}
              />
            ))}
          </Box>
        </Box>
      </Popover>

      {/* Regions Popover */}
      <Popover
        open={regionsPopover.open}
        anchorEl={regionsPopover.anchorEl}
        onClose={() => setRegionsPopover({ open: false, anchorEl: null })}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{
          sx: {
            width: 300,
            maxHeight: 400,
            borderRadius: "8px",
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
          },
        }}
      >
        <Box sx={{ p: 1.5 }}>
          <TextField
            size="small"
            placeholder="Search regions..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: "#9ca3af" }} />
                </InputAdornment>
              ),
            }}
            fullWidth
            sx={{ mb: 1 }}
          />
          <Box sx={{ maxHeight: 300, overflowY: "auto" }}>
            {regions.map((region) => (
              <FormControlLabel
                key={region}
                control={
                  <Checkbox
                    checked={formData.selectedRegions.includes(region)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        updateField("selectedRegions", [...formData.selectedRegions, region]);
                      } else {
                        updateField("selectedRegions", formData.selectedRegions.filter((r) => r !== region));
                      }
                    }}
                    size="small"
                  />
                }
                label={region}
                sx={{
                  display: "flex",
                  width: "100%",
                  m: 0,
                  py: 0.5,
                }}
              />
            ))}
          </Box>
        </Box>
      </Popover>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default EditBillingRuleModal;
